// @givmo/app-charity — CSV download helpers (CPA-WEB5; extended for CPB-WEB6's CRM exports).
//
// The app's CSV-download fragment: the two §321 statement exports and the charity-owned analytics
// export. The CRM package owns its two module exports after CRM-1. This helper stays app-local
// (see below) but is NOT re-copied per feature — one implementation means one place where a filename
// is synthesized and one place where a failed export is turned into a thrown error.
//
// Every one of them returns the CSV as a BINARY body (`Blob | File`), so they need the response-mode
// opt-out the super-admin ZIP export uses: `parseAs:'blob'` + `responseStyle:'fields'` (raw `Response` so
// the Content-Disposition filename can be read) via the package's `readBinaryMeta`. These are short,
// unauthenticated-body GETs (no request body, no long timeout, no forced token refresh — unlike the
// GCF ZIP), so the fragment is deliberately minimal.
//
// `runCsvDownload` runs one export SDK call, reads the suggested filename (falling back to a synthesized
// name), and triggers an in-browser Blob download via a transient object-URL <a download> anchor
// (mirrors super-admin's `blobSave`). It THROWS on an errored/absent response so the caller can toast.

import { readBinaryMeta, type Options } from '@givmo/api-client';

/**
 * Per-request fragment opting a single §321 CSV export OUT of JSON parsing and into a BINARY (blob)
 * response WITH the raw `Response` exposed (`responseStyle:'fields'`), so the caller can read the
 * Content-Disposition filename via {@link readBinaryMeta}. Same shape super-admin's `binaryDownloadOptions`
 * uses; kept local to the charity app so we don't reach into another app's `lib/`.
 */
export const csvDownloadOptions = {
  parseAs: 'blob',
  responseStyle: 'fields',
} as const satisfies Pick<Options, 'parseAs' | 'responseStyle'>;

/** The result shape of a configured (non-throwing) SDK call with `responseStyle:'fields'`. */
interface CsvExportResult {
  data?: unknown;
  error?: unknown;
  response?: Response;
}

/** Any function that, given the blob response-mode options, runs one CSV export SDK call. */
export type CsvExportCall = (options: typeof csvDownloadOptions) => Promise<CsvExportResult>;

/** Trigger an in-browser download of `blob` as `filename` via a transient object-URL anchor. */
export function blobSave(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * Run one §321 CSV export and save it. `call` receives {@link csvDownloadOptions} and returns the
 * configured SDK result (`{ data, error, response }`). Reads the server's Content-Disposition filename
 * when present, else uses `fallbackFilename`. Throws on an errored or missing response so the caller
 * surfaces a toast and never triggers a phantom download.
 */
export async function runCsvDownload(call: CsvExportCall, fallbackFilename: string): Promise<void> {
  await runBinaryDownload(call, fallbackFilename, 'text/csv');
}

/**
 * The general form: run one binary export and save it, whatever its media type.
 *
 * `runCsvDownload` is this with `text/csv`, and the §321 WORKBOOK (CPA-BE15/WEB11) is this with the
 * spreadsheetml type. Kept as one implementation rather than two near-copies, because the parts worth
 * getting right are shared and easy to get subtly wrong twice: an errored or absent response has to
 * THROW (so the caller toasts instead of triggering a phantom download of an error page), and the
 * server's Content-Disposition filename has to win over the synthesized fallback (the workbook's is
 * date-stamped server-side, and a client-invented name would disagree with it).
 *
 * `fallbackMime` types the defensive non-Blob branch only. It is never used to override a real Blob's
 * own type — a `.xlsx` handed to `blobSave` as `text/csv` opens as garbage in Excel.
 */
export async function runBinaryDownload(
  call: CsvExportCall,
  fallbackFilename: string,
  fallbackMime: string,
): Promise<void> {
  const result = await call(csvDownloadOptions);
  if (result.error) throw result.error;
  if (!result.response) throw new Error('Export failed: no response.');

  const meta = readBinaryMeta(result.response);
  const filename = meta.filename ?? fallbackFilename;
  // `data` is the Blob (parseAs:'blob'); guard defensively for a text body.
  const blob =
    result.data instanceof Blob
      ? result.data
      : new Blob([(result.data ?? '') as BlobPart], { type: fallbackMime });
  blobSave(blob, filename);
}

/** The exports this fragment serves: the two §321 statement CSVs plus donor analytics. */
export type CsvExportKind = 'grants' | 'donors' | 'analytics-yearly';

const CSV_NAME_STEMS: Record<CsvExportKind, string> = {
  grants: 'givmo_grants',
  donors: 'givmo_consenting_donors',
  'analytics-yearly': 'givmo_analytics_yearly',
};

/** Synthesize a fallback CSV filename (stamped) when the response carries no Content-Disposition. */
export function fallbackCsvName(kind: CsvExportKind): string {
  const stamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  return `${CSV_NAME_STEMS[kind]}_${stamp}.csv`;
}
